import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  User, IndianRupee, Calendar, Clock, AlertTriangle, 
  CheckCircle, FileText, TrendingUp, ChevronRight,
  Phone, Bell, Baby, Star, MessageSquare
} from 'lucide-react';
import ObserverNav from '../components/ObserverNav';
import UpcomingMeetingsWidget from '../components/UpcomingMeetingsWidget';
import ObserverScheduledCalls from '../components/ObserverScheduledCalls';
import CallHistory from '../components/CallHistory';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [todaySessions, setTodaySessions] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [earnings, setEarnings] = useState({ total: 0, thisMonth: 0, pending: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('observer_token');
    if (!token) {
      navigate('/observer/login');
      return;
    }
    loadAllData();
  }, [navigate]);

  const loadAllData = async () => {
    try {
      const token = localStorage.getItem('observer_token');
      
      const [dashboardRes, earningsMonthRes, earningsTotalRes, auditsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/observer/dashboard?token=${token}`),
        axios.get(`${BACKEND_URL}/api/observer/earnings?token=${token}&period=month`).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/observer/earnings?token=${token}&period=all`).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/audit/observer/pending?token=${token}`).catch(() => ({ data: { audits: [] } }))
      ]);
      
      setData(dashboardRes.data);
      
      // Set earnings - monthly and total
      const monthlyData = earningsMonthRes.data?.earnings || {};
      const totalData = earningsTotalRes.data?.earnings || {};
      setEarnings({
        total: totalData.total_earned || 0,
        thisMonth: monthlyData.total_earned || 0,
        pending: totalData.pending || 0
      });
      
      // Get today's scheduled sessions - only show actual scheduled sessions, not mock data
      // In real implementation, this would come from an appointments/schedule API
      // For now, show empty state since no real scheduling system exists
      setTodaySessions([]);
      
      // Set pending tasks - only if there are actual pending items
      const pendingAudits = auditsRes.data.audits || [];
      const tasks = [];
      
      // Add pending audits only (no fake report tasks)
      pendingAudits.forEach(audit => {
        if (audit.status === 'pending') {
          tasks.push({
            type: 'audit',
            title: `Complete audit review`,
            audit_id: audit.id,
            priority: 'medium'
          });
        }
      });
      
      setPendingTasks(tasks.slice(0, 5));
      
      // Set alerts (would come from escalations/red flags API)
      setAlerts([]);
      
    } catch (error) {
      console.error('Error loading dashboard:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('observer_token');
        navigate('/observer/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (!data) return null;

  const { observer, children, statistics } = data;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="observer-dashboard">
      <ObserverNav />
      
      <div className="pt-16 pb-8 px-4">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-slate-800">
              {getGreeting()}, {observer?.name?.split(' ')[0]}
            </h1>
            <p className="text-slate-500">{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </div>

          {/* Upcoming meetings (Principal-scheduled, observer is attendee) */}
          <div className="mb-6">
            <UpcomingMeetingsWidget role="observer" token={localStorage.getItem('observer_token')} />
          </div>

          {/* Quick Stats with Income */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-100 text-sm">This Month</p>
                    <p className="text-2xl font-bold">{formatCurrency(earnings.thisMonth)}</p>
                  </div>
                  <IndianRupee className="w-10 h-10 text-emerald-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-green-600 to-green-700 text-white border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm">Total Earnings</p>
                    <p className="text-2xl font-bold">{formatCurrency(earnings.total)}</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-green-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-violet-500 to-violet-600 text-white border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-violet-100 text-sm">My Children</p>
                    <p className="text-2xl font-bold">{statistics?.total_children || children?.length || 0}</p>
                  </div>
                  <Baby className="w-10 h-10 text-violet-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">This Week</p>
                    <p className="text-2xl font-bold">{statistics?.sessions_this_week || 0}</p>
                  </div>
                  <Calendar className="w-10 h-10 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-100 text-sm">Avg Rating</p>
                    <p className="text-2xl font-bold flex items-center gap-1">
                      <Star className="w-5 h-5" fill="white" />
                      {statistics?.average_rating || '4.5'}
                    </p>
                  </div>
                  <Star className="w-10 h-10 text-amber-200" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            
            {/* Scheduled Calls - Full width on top */}
            <div className="lg:col-span-2">
              <ObserverScheduledCalls />
            </div>

            {/* Call History - Full width */}
            <div className="lg:col-span-2">
              <CallHistory userType="observer" token={localStorage.getItem('observer_token')} />
            </div>

            {/* Pending Tasks / Action Items */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between text-lg">
                  <span className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-amber-600" />
                    Pending Tasks
                    {pendingTasks.length > 0 && (
                      <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{pendingTasks.length}</span>
                    )}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pendingTasks.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                    <p>All caught up!</p>
                    <p className="text-sm">No pending tasks</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingTasks.map((task, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-center justify-between p-3 rounded-lg hover:opacity-90 cursor-pointer border-l-4 ${
                          task.type === 'report' ? 'bg-blue-50 border-blue-500' : 
                          task.type === 'audit' ? 'bg-violet-50 border-violet-500' : 'bg-slate-50 border-slate-400'
                        }`}
                        onClick={() => {
                          if (task.type === 'report') navigate(`/observer/report/${task.child_id}`);
                          else if (task.type === 'audit') navigate('/observer/audits');
                        }}
                      >
                        <div className="flex items-center gap-3">
                          {task.type === 'report' ? (
                            <FileText className="w-5 h-5 text-blue-600" />
                          ) : (
                            <MessageSquare className="w-5 h-5 text-violet-600" />
                          )}
                          <div>
                            <p className="font-medium text-slate-800">{task.title}</p>
                            <p className="text-xs text-slate-500 capitalize">{task.priority} priority</p>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Alerts / Red Flags */}
            <Card className="lg:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between text-lg">
                  <span className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    Alerts & Escalations
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/observer/escalations')}>
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {alerts.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                    <p>No active alerts</p>
                    <p className="text-sm">All children are doing well</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-3">
                    {alerts.slice(0, 4).map((alert, idx) => (
                      <div key={alert.id || idx} className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border-l-4 border-red-500">
                        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-slate-800">{alert.child_name}</p>
                          <p className="text-sm text-slate-600">{alert.description}</p>
                          <p className="text-xs text-slate-400 mt-1">{alert.date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>


        </div>
      </div>
    </div>
  );
};

export default ObserverDashboard;
